// Das kompakte Projekt-Band plus die einklappbaren Projekt-Details.
//
// WOFUER — der gemessene Befund: auf der Projektseite sass die erste
// Aufgabenzeile bei y=759 von 900 px. Davor stapelten sich acht Baender, drei
// davon allein hier: der hohe „Hero"-Kasten (Titel, Pillen, Owner, Mitglieder,
// vier Knoepfe), die drei Info-Karten (Beschreibung / Anhaenge / Datei-Upload)
// und die Fortschrittskarte. Die letzten zwei kosteten zusammen ~330 px.
//
// ENTSCHEIDUNG: Band + einklappbar, standardmaessig ZUGEKLAPPT — und zwar
// beides, nicht eines von beiden. Begruendung: der teuerste Einzelposten ist
// der gestrichelte Datei-Upload-Kasten (~150 px), der auf einer Aufgabenseite
// selten gebraucht wird und deshalb nicht dauerhaft offen stehen muss; die
// Angaben, die man dagegen IMMER sehen will (Status, Zieldatum,
// Gesamtfortschritt, wer zustaendig ist), sind zu EINER Zeile verdichtet, die
// immer sichtbar bleibt. Ein reines Zuklappen ohne dieses Band haette
// Zieldatum und Fortschritt verschwinden lassen — Information darf hier
// nirgends verloren gehen, nur die Bauhoehe.
//
// Der Zustand wird pro Projekt in `localStorage` gemerkt, nach dem Muster der
// Seitenleiste fuer ihre Gruppen (`components/Sidebar.tsx`): ein Signal als
// Lese-Cache, `localStorage` als Gedaechtnis, Abwesenheit == Standard.
//
// Zugleich fallen hier SECHS Kapitaelchen-Etiketten aus dem Standardbild
// (BESCHREIBUNG, ANHAENGE, DATEI-UPLOAD, STATUS, ZIELDATUM,
// GESAMTFORTSCHRITT) — sie stecken in `attachments/ProjectTopCards` und
// rendern nur noch im aufgeklappten Zustand. Das Band selbst hat keine.

import type { JSX } from "preact";
import { signal } from "@preact/signals";
import { ChevronDown, ChevronRight, Eye, Star, StarOff } from "lucide-preact";
import type { ProjectWithCounts } from "@juuplan/shared";
import { StatusBadge } from "../../components/ProjectCard";
import { HealthBadge } from "../../components/HealthBadge";
import { ProjectPeople } from "../../components/ProjectPeople";
import { ProjectTopCards } from "../../components/attachments/ProjectTopCards";
import { formatDateShort } from "../../lib/format";

// ---- Aufklapp-Zustand der Details (pro Projekt gemerkt) -------------------
// Muster wie `SPACE_GROUP_PREFIX` in components/Sidebar.tsx.

const DETAILS_PREFIX = "juuplan:project-details:";

/** Standard ist ZUGEKLAPPT — Abwesenheit des Eintrags heisst also „zu". */
function readDetailsOpen(id: number): boolean {
  if (typeof localStorage === "undefined") return false;
  try {
    return localStorage.getItem(DETAILS_PREFIX + id) === "1";
  } catch {
    // Privater Modus / gesperrter Speicher: Standard statt Absturz.
    return false;
  }
}

/** Ein Signal als Lese-Cache: projektId -> offen. Traege gefuellt. */
const detailsOpenSig = signal<Record<number, boolean>>({});

/** Reiner Lesezugriff — schreibt NICHT in das Signal waehrend des Renderns. */
function useDetailsOpen(id: number): boolean {
  return detailsOpenSig.value[id] ?? readDetailsOpen(id);
}

function toggleDetailsOpen(id: number): void {
  const cur = detailsOpenSig.value[id] ?? readDetailsOpen(id);
  const next = !cur;
  detailsOpenSig.value = { ...detailsOpenSig.value, [id]: next };
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(DETAILS_PREFIX + id, next ? "1" : "0");
  } catch {
    // Nicht schreibbar — der Zustand haelt dann nur diese Sitzung.
  }
}

export interface ProjectOverviewProps {
  project: ProjectWithCounts;
  readOnly: boolean;
  canShare: boolean;
  isFavorite: boolean;
  onToggleFavorite: () => void;
  /** Oeffnet den Einstellungen-Bereich („Bearbeiten" in der Beschreibung). */
  onEdit: () => void;
}

export function ProjectOverview(props: ProjectOverviewProps): JSX.Element {
  const {
    project,
    readOnly,
    canShare,
    isFavorite,
    onToggleFavorite,
    onEdit,
  } = props;

  const detailsOpen = useDetailsOpen(project.id);

  const total = project.tasks_total;
  const done = project.tasks_done;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div class="mb-4">
      {/* DAS BAND — eine Zeile statt eines hohen Kastens. Der frueher hier
          stehende Kasten trug denselben Inhalt auf ~96 px Mindesthoehe plus
          Innenabstand; der farbige Streifen links bleibt als Projekt-Identitaet. */}
      <div
        class="flex items-start gap-3 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] py-2 pl-3 pr-2"
        style={{
          borderLeftWidth: "3px",
          borderLeftColor: project.color ?? "var(--color-primary)",
        }}
        data-testid="project-meta-band"
      >
        {/* Inhalt bricht um, Favorit + Details bleiben oben rechts stehen —
            vorher rutschten sie bei zwei Personen-Auswahlen allein in eine
            zweite Zeile und liessen eine leere Flaeche zurueck. */}
        <div class="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-2">
        <StatusBadge status={project.status} />
        <HealthBadge project={project} />

        {readOnly && (
          <span
            class="inline-flex items-center gap-1 rounded-full bg-[color:var(--color-surface-container-high)] px-2 py-0.5 text-label font-medium text-[color:var(--color-on-surface-variant)]"
            title="Du hast nur Lese-Zugriff auf dieses Projekt"
          >
            <Eye size={14} aria-hidden="true" />
            Nur-Lese-Zugriff
          </span>
        )}

        {/* Zieldatum — stand vorher als Kapitaelchen-Etikett „ZIELDATUM" in
            Karte 1 und waere beim Zuklappen verschwunden. */}
        <span class="text-xs text-[color:var(--color-on-surface-variant)]">
          Ziel:{" "}
          <span class="font-medium text-[color:var(--color-on-surface)]">
            {formatDateShort(project.target_date)}
          </span>
        </span>

        {/* Gesamtfortschritt — vorher eine eigene Karte („GESAMTFORTSCHRITT")
            in voller Breite, jetzt Zahl plus schmaler Balken in der Zeile. */}
        <span
          class="inline-flex items-center gap-2 text-xs text-[color:var(--color-on-surface-variant)]"
          data-testid="project-progress"
        >
          <span
            class="h-1.5 w-16 overflow-hidden rounded-full bg-[color:var(--color-surface-container-low)]"
            aria-hidden="true"
          >
            <span
              class="block h-full rounded-full"
              style={{
                width: `${pct}%`,
                background: project.color ?? "var(--color-primary)",
              }}
            />
          </span>
          <span class="font-medium text-[color:var(--color-on-surface)]">
            {done}/{total} ({pct}%)
          </span>
        </span>

        {/* #854: Owner wechselbar + Mitglieder + „Wer hat Zugriff?" — bleiben
            im Inhalt, nicht in der Kopfzeile (die traegt schon fuenf Aktionen). */}
        <ProjectPeople project={project} canManage={canShare} />
        </div>

        <div class="ml-auto flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onToggleFavorite}
            class={`inline-flex items-center justify-center rounded p-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 ${
              isFavorite
                ? "text-[color:var(--color-warning)] hover:bg-[color:var(--color-surface-container-low)]"
                : "text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] hover:text-[color:var(--color-on-surface)]"
            }`}
            aria-label={
              isFavorite
                ? "Projekt aus Favoriten entfernen"
                : "Projekt zu Favoriten hinzufügen"
            }
            aria-pressed={isFavorite}
            title={isFavorite ? "Favorit entfernen" : "Als Favorit markieren"}
          >
            {isFavorite ? (
              <Star size={16} fill="currentColor" aria-hidden="true" />
            ) : (
              <StarOff size={16} aria-hidden="true" />
            )}
          </button>

          <button
            type="button"
            onClick={() => toggleDetailsOpen(project.id)}
            aria-expanded={detailsOpen}
            aria-controls={`project-details-${project.id}`}
            class="inline-flex min-h-[36px] items-center gap-1 rounded px-2 py-1 text-xs font-medium text-[color:var(--color-on-surface-variant)] transition-colors hover:bg-[color:var(--color-surface-container-low)] hover:text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] sm:min-h-[28px]"
            title="Beschreibung, Anhänge und Datei-Upload ein-/ausklappen"
            data-testid="project-details-toggle"
          >
            {detailsOpen ? (
              <ChevronDown size={14} aria-hidden="true" />
            ) : (
              <ChevronRight size={14} aria-hidden="true" />
            )}
            Details
          </button>
        </div>
      </div>

      {/* Beschreibung / Anhaenge / Datei-Upload — nur im aufgeklappten
          Zustand im Baum. Bewusst NICHT nur per CSS versteckt: der
          Upload-Bereich und die Anhangsliste holen beim Rendern Daten. */}
      {detailsOpen && (
        <div id={`project-details-${project.id}`} class="mt-3">
          <ProjectTopCards
            project={project}
            readOnly={readOnly}
            onEdit={onEdit}
          />
        </div>
      )}
    </div>
  );
}
